/**
 * JoinSplit Key Derivation
 * Implements Railgun-style hierarchical key derivation
 */

import { ethers } from "ethers";
import createBlakeHash from "blake-hash";
import type {
  ShareableViewingKeyPayload,
  SpendingKeys,
  ViewingKeys,
} from "./types";

let eddsa: any;
let poseidon: any;
let babyJub: any;

/**
 * Initialize circomlibjs modules
 */
async function initCrypto() {
  if (!eddsa || !poseidon || !babyJub) {
    // Dynamic import with type assertion to access all exports
    const circomlibjs = (await import("circomlibjs")) as unknown as {
      buildEddsa: () => Promise<any>;
      buildPoseidon: () => Promise<any>;
      buildBabyjub: () => Promise<any>;
    };
    eddsa = await circomlibjs.buildEddsa();
    poseidon = await circomlibjs.buildPoseidon();
    babyJub = await circomlibjs.buildBabyjub();
  }
  return { eddsa, poseidon, babyJub };
}

/**
 * Derive spending keys from a seed (e.g., from wallet signature)
 *
 * @param seed - 32 bytes of entropy
 * @returns SpendingKeys structure
 */
export async function deriveSpendingKeys(
  seed: Uint8Array,
): Promise<SpendingKeys> {
  const { eddsa, poseidon } = await initCrypto();

  // The seed becomes our spending key
  const spendingKey = seed;

  // Derive EdDSA keypair from spending key
  // EdDSA private key is derived by hashing the spending key
  const privateKey = eddsa.pruneBuffer(
    Buffer.from(ethers.keccak256(spendingKey).slice(2), "hex").slice(0, 32),
  );

  // Get public key (point on BabyJubJub)
  const publicKeyPoint = eddsa.prv2pub(privateKey);
  const publicKey: [bigint, bigint] = [
    BigInt(eddsa.F.toObject(publicKeyPoint[0]).toString()),
    BigInt(eddsa.F.toObject(publicKeyPoint[1]).toString()),
  ];

  // Derive nullifying key from spending key (separate derivation path)
  const nullifyingKeyBytes = ethers.keccak256(
    ethers.concat([spendingKey, ethers.toUtf8Bytes("nullifying")]),
  );
  const nullifyingKey =
    BigInt(nullifyingKeyBytes) % BigInt(eddsa.F.p.toString());

  // Compute Master Public Key = Poseidon(Ax, Ay, nullifyingKey)
  const mpkHash = poseidon([publicKey[0], publicKey[1], nullifyingKey]);
  const mpk = BigInt(poseidon.F.toObject(mpkHash).toString());

  return {
    spendingKey,
    publicKey,
    nullifyingKey,
    mpk,
    spendSecretScalar: deriveSpendSecretScalar(eddsa, privateKey),
  };
}

/**
 * The Baby Jubjub scalar `a` with `publicKey = a * Base8`.
 *
 * The JoinSplit circuit proves knowledge of this value, which is what makes a
 * spend an act of authority rather than an act of knowing the note's fields.
 * It reproduces circomlibjs `prv2pub` internals exactly — blake512, prune, read
 * little-endian, shift right 3 — because it must reproduce the same public key
 * the note was addressed to. Changing this derivation orphans every note.
 */
function deriveSpendSecretScalar(eddsa: any, privateKey: Uint8Array): bigint {
  const pruned = eddsa.pruneBuffer(
    createBlakeHash("blake512")
      .update(Buffer.from(privateKey))
      .digest()
      .slice(0, 32),
  );
  let value = 0n;
  for (let i = 31; i >= 0; i--) {
    value = (value << 8n) | BigInt(pruned[i]);
  }
  return value >> 3n;
}

/**
 * Derive the note-decryption viewing key from the spending key.
 *
 * This must never be the spending key itself: it is handed to accountants and
 * used to key the encrypted inbox, and the spending key is the seed of the
 * whole account. Same domain-separated keccak path as the nullifying key.
 *
 * @param spendingKey - Spending key bytes
 * @returns 32-byte viewing key
 */
export function deriveViewingKey(spendingKey: Uint8Array): Uint8Array {
  return ethers.getBytes(
    ethers.keccak256(
      ethers.concat([spendingKey, ethers.toUtf8Bytes("viewing")]),
    ),
  );
}

function formatMpkHex(mpk: bigint): string {
  return `0x${mpk.toString(16).padStart(64, "0")}`;
}

function assertShareableViewingKeyPayload(
  payload: ShareableViewingKeyPayload,
): ShareableViewingKeyPayload {
  if (payload.version !== 1) {
    throw new Error("Unsupported shareable viewing key version");
  }
  if (!/^0x[0-9a-fA-F]{64}$/.test(payload.mpk)) {
    throw new Error("Invalid shareable viewing key mpk");
  }
  if (!Array.isArray(payload.publicKey) || payload.publicKey.length !== 2) {
    throw new Error("Invalid shareable viewing key public key");
  }
  if (
    !payload.publicKey.every(
      (value) => typeof value === "string" && value.trim().length > 0,
    )
  ) {
    throw new Error("Invalid shareable viewing key public key");
  }
  return payload;
}

/**
 * Compute Note Public Key (NPK) for a new note
 * NPK = Poseidon(MPK, random)
 *
 * @param mpk - Master Public Key
 * @param random - Random value for this note
 * @returns NPK
 */
export async function computeNPK(mpk: bigint, random: bigint): Promise<bigint> {
  const { poseidon } = await initCrypto();
  const hash = poseidon([mpk, random]);
  return BigInt(poseidon.F.toObject(hash).toString());
}

/**
 * Compute note commitment
 * commitment = Poseidon(NPK, tokenHash, value)
 *
 * @param npk - Note Public Key
 * @param tokenHash - Poseidon hash of token address
 * @param value - Note value
 * @returns commitment as hex string
 */
export async function computeCommitment(
  npk: bigint,
  tokenHash: bigint,
  value: bigint,
): Promise<string> {
  const { poseidon } = await initCrypto();
  const hash = poseidon([npk, tokenHash, value]);
  const commitment = BigInt(poseidon.F.toObject(hash).toString());
  return "0x" + commitment.toString(16).padStart(64, "0");
}

/**
 * Compute nullifier for a note
 * nullifier = Poseidon(nullifyingKey, tokenHash, leafIndex)
 *
 * @param nullifyingKey - User's nullifying key
 * @param tokenHash - Poseidon hash of token address
 * @param leafIndex - Leaf index in Merkle tree
 * @returns nullifier as hex string
 */
export async function computeNullifier(
  nullifyingKey: bigint,
  tokenHash: bigint,
  leafIndex: number,
): Promise<string> {
  const { poseidon } = await initCrypto();
  const hash = poseidon([nullifyingKey, tokenHash, BigInt(leafIndex)]);
  const nullifier = BigInt(poseidon.F.toObject(hash).toString());
  return "0x" + nullifier.toString(16).padStart(64, "0");
}

/**
 * Get the EdDSA private key from spending key for signing
 *
 * @param spendingKey - Spending key bytes
 * @returns Pruned private key for EdDSA signing
 */
export async function getSigningKey(spendingKey: Uint8Array): Promise<Buffer> {
  const { eddsa } = await initCrypto();
  return eddsa.pruneBuffer(
    Buffer.from(ethers.keccak256(spendingKey).slice(2), "hex").slice(0, 32),
  );
}
